import React from "react";
import * as CONSTANT from "../../utils/constant";
import CardLayout from "react-lib/apps/common/CardLayout";
import SubFormViewer from "react-lib/apps/PTM/SubFormViewer";
import { Label } from "semantic-ui-react";
import { useIntl } from "react-intl";

interface CardPainAssessmentProps {
 onClose?: () => void;
 controller?: Record<string,any>;
 encounterId?: string | number;
 showSummary?: boolean;
 painOptions?: any[];
}

const DEFAULT_PROPS = {
  onClose: () => {},
  controller: {},
  encounterId: null,
  showSummary: false,
}
const CardPainAssessment = React.forwardRef<any,CardPainAssessmentProps>((propsParam, ref) => {
  const props = {
    ...DEFAULT_PROPS,
    ...Object.fromEntries(Object.entries(propsParam).filter(([_, value])=> value !== undefined))
  }
  const [isLoading, setIsLoading] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const [painAssessmentData, setPainAssessmentData] = React.useState<any[]>([]);
  const [subFormViewData, setSubFormViewerData] = React.useState<any[]>([]);

  React.useImperativeHandle(ref, () => ({
    refresh: ({ encounterId } = {}) => {
      refresh({ encounterId });
    },
  }));

  React.useEffect(() => {
    refresh({ encounterId: props.encounterId });
  }, [props.encounterId]);

  React.useEffect(() => {
    formatFormViewData();
  }, [painAssessmentData]);

  const refresh = ({ encounterId } = {}) => {
    getFormDataLatest({ encounterId });
  };

  const painAssessmentOptions = (props.painOptions || []).map((item: any) => ({
    ...item,
    key: item?.key.toString(),
    value: item?.value.toString(),
  }));

  const painFrequencyOptions = (props.painFreqOptions || []).map((item: any) => ({
    ...item,
    key: item?.key.toString(),
    value: item?.value.toString(),
  }));

  const formatFormViewData = () => {
    let newArr = [...painAssessmentData];
    newArr.forEach((item) => {
      item.pain_assessment_tool = painAssessmentOptions.find(
        (row: any) => row.value.toString() === item?.pain_assessment_tool
      )?.text;

      item.frequency = painFrequencyOptions.find(
        (row: any) => row.value.toString() === item?.frequency
      )?.text;
    });
    setSubFormViewerData(newArr);
  };

  const getFormDataLatest = async ({ encounterId }) => {
    setIsLoading(true);
    const [data, error] = await props.controller.getFormDataLatest({
      encounterId: encounterId ? encounterId : props.patientData.ENCOUNTER.encounter_id,
      formCode: "CardPainAssessment",
      formVersion: "0.1",
    });
    if (data) {
      let newData = data.data.map((item: any) => {
        let newNursing = "";
        item?.nursing.forEach((nursingData: any) => {
          if (newNursing === "") {
            newNursing = `${NURSING[nursingData]}`;
          } else {
            newNursing = `${newNursing}, ${NURSING[nursingData]}`;
          }
        });

        let min = item?.duration_min > 0 ? `${item?.duration_min} นาที` : "";
        let sec = item?.duration_sec > 0 ? `${item?.duration_sec} วินาที` : "";

        return {
          ...item,
          nursing: newNursing,
          duration: `${min} ${sec}`,
        };
      });

      setStatus(data.status);
      setPainAssessmentData(newData);
    }
    setIsLoading(false);
  };

  let statusColor = "black";
  if (status === CONSTANT.FORM_DATA_STATUS.DRAFT) {
    statusColor = "yellow";
  } else if (status === CONSTANT.FORM_DATA_STATUS.EDITED) {
    statusColor = "olive";
  } else if (status === CONSTANT.FORM_DATA_STATUS.CONFIRMED) {
    statusColor = "green";
  } else if (status === CONSTANT.FORM_DATA_STATUS.CANCELLED) {
    statusColor = "red";
  }

  let labelStatus = <Label color={statusColor}>{status}</Label>;

  return (
    <CardLayout
      headerColor="grey"
      loading={isLoading}
      titleText="Pain Assessment"
      onClose={props.onClose}
      titleContent={labelStatus}
    >
      {props.showSummary ? (
        <SubFormViewer fieldList={rmdFieldList} fieldMap={rmdFieldMap} data={subFormViewData} languageUX={props.languageUX}/>
      ) : (
        "TODO ---> Implement ShowSummary = false"
      )}
    </CardLayout>
  );
});

CardPainAssessment.displayName = "CardPainAssessment"

const NURSING = {
  NA: "NA",
  NON: "ไม่จำเป็น",
  REL: "จัดท่าเพื่อลดความเจ็บปวด",
  HCP: "วาง Hot/Cold pack",
  REP: "รายงานแพทย์ pain score > 3",
  YRX: "มี Rx",
  NRX: "ไม่มี Rx",
  Other: "อื่น ๆ",
};

const rmdFieldList = [
  "user_fullname",
  "date",
  "pain_score",
  "pain_assessment_tool",
  "location",
  "characteristic",
  "frequency",
  "duration",
  "nursing",
  "nursing_remark",
];

const rmdFieldMap = {
  user_fullname: "ผู้บันทึก/แก้ไข",
  pain_score: "Pain Score",
  pain_assessment_tool: "Pain Assessment Tool",
  date: "Date Time",
  location: "Pain Location",
  characteristic: "Characteristic",
  frequency: "Frequency",
  duration: "Duration (ครั้งละประมาณ)",
  nursing: "การให้การพยาบาลผู้ป่วย",
  nursing_remark: "ระบุ",
};


export default React.memo(CardPainAssessment);
